// src/ui/components/help/HelpFAB.tsx

import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HelpCircle,
  BookOpen,
  MessageCircleQuestion,
  RotateCcw,
} from "lucide-react";
import { useIsMobile } from "@/controllers/hooks/use-mobile";

const HIDDEN_PATHS = [
  "/login",
  "/cadastro",
  "/recuperar",
  "/boas-vindas",
  "/admin",
];

export function HelpFAB() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // Hide on certain pages
  const shouldHide = HIDDEN_PATHS.some((path) =>
    location.pathname.startsWith(path),
  );

  // Close menu on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  if (shouldHide || isMobile) return null;

  const menuItems = [
    {
      icon: BookOpen,
      label: "Guias & Tutoriais",
      onClick: () => {
        navigate("/ajuda");
        setIsOpen(false);
      },
    },
    {
      icon: MessageCircleQuestion,
      label: "Perguntas Frequentes",
      onClick: () => {
        navigate("/ajuda#faq");
        setIsOpen(false);
      },
    },
    {
      icon: RotateCcw,
      label: "Refazer o Tour",
      onClick: () => {
        window.dispatchEvent(new CustomEvent("restart-product-tour"));
        setIsOpen(false);
      },
    },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed bottom-6 right-6 z-50"
      data-tour="help-fab"
    >
      {/* Mini Menu */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-56 bg-white rounded-xl shadow-figma-lg border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div className="p-2">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={item.onClick}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:text-accent-600 transition-colors"
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* FAB Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-12 h-12 rounded-full bg-gradient-to-br from-accent-600 to-amber-700 text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 flex items-center justify-center"
        aria-label="Ajuda"
      >
        <HelpCircle className="w-6 h-6" />
      </button>
    </div>
  );
}
